import React, {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  UIEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import classes from './NavScroller.module.scss';
import Typography from 'new/elements/Typography';
import cx from 'classnames';
import { useNavScrollerHeight } from 'new/hooks/useNavScrollerHeight';
import { isEqual } from 'lodash';

export type NavOptionType = {
  id?: string;
  label?: string;
  /**
   * Optional ReactNode that renders as an additional element
   * below the NavOption component.
   * If you set the element, you don't need to provide the id and label property.
   *
   * Example:
   *  <NavOption {...props} />
   *  {element}
   */
  element?: ReactNode;
};

interface NavScrollerProps {
  navOptions: NavOptionType[];
  navClassName?: string;
  containerClassName?: string;
  navContentClassName?: string;
  offset?: number;
  style?: CSSProperties | undefined;
  isColumnLayout?: boolean;
}

type NavOptionProps = NavOptionType & { active: boolean; onClick: () => void };

function NavOption(props: NavOptionProps) {
  const { label, active, onClick } = props;

  return (
    <div onClick={onClick} className={cx(classes.option, { [classes.option_active]: active })}>
      <Typography variant={active ? 'subtitle2' : 'bodyDefault'} className={classes.optionLabel}>
        {label}
      </Typography>
    </div>
  );
}

function handleOffsetTops(navOptions: NavOptionType[], offset = 0) {
  return navOptions.reduce((object, option) => {
    if (option?.id) {
      object[option.id] = document.getElementById(option.id)?.offsetTop - offset;
    }
    return object;
  }, {});
}

export const NavScrollerContext = React.createContext(null);
NavScrollerContext.displayName = 'NavScrollerContext';

export const NavScroller = (props: PropsWithChildren<NavScrollerProps>) => {
  const {
    children,
    navOptions,
    navClassName = '',
    offset = 0,
    containerClassName,
    navContentClassName,
    style,
    isColumnLayout = false,
  } = props;
  const clickScrollRef = useRef(false);
  const clickScrollTimeout = useRef(null);
  const refScroll = useRef<HTMLDivElement>();
  const resizeRef = useRef<HTMLDivElement>();
  const height = useNavScrollerHeight(refScroll, 0);
  const [activeScroll, setActive] = useState(navOptions?.[0]?.id);
  const [offsetsTop, setTops] = useState({});

  const onScrollToElement = (elementID: string) => {
    let element = document.getElementById(elementID);

    if (!element) {
      setTimeout(() => {
        element = document.getElementById(elementID);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 500);
    } else {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 100);
    }
  };

  const onScrollTo = (elementID: string) => () => {
    clickScrollRef.current = true;

    setActive(elementID);

    if (refScroll.current) {
      const base = document.getElementById('negative-element');
      if (base) {
        base.style.height = offsetsTop[elementID] + 'px';
      }

      onScrollToElement(elementID);
    }
  };

  function updateOffsets() {
    const newOffsetsTop = handleOffsetTops(navOptions, offset);
    const isNotEquals = !isEqual(newOffsetsTop, offsetsTop);

    if (isNotEquals) {
      setTops(newOffsetsTop);
    }
  }

  const onResize = () => {
    updateOffsets();
  };

  useEffect(() => {
    updateOffsets();
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(resizeRef.current);
    return () => {
      if (resizeRef.current instanceof Element) {
        resizeObserver.unobserve(resizeRef.current);
      }
    };
  }, [navOptions, updateOffsets, onResize, resizeRef]);

  const onScroll = (ev: UIEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !clickScrollRef.current) {
      const keys = navOptions.map((option) => option.id);
      for (let i = keys.length - 1; i >= 0; i--) {
        const scrollTop = (ev.target as HTMLDivElement).scrollTop;
        if (scrollTop >= offsetsTop[keys[i]]) {
          setActive(keys[i]);
          break;
        }
      }
    } else {
      clearTimeout(clickScrollTimeout.current);
      clickScrollTimeout.current = setTimeout(() => {
        clickScrollRef.current = false;
      }, 50);
    }
  };

  return (
    <NavScrollerContext.Provider value={{ activeScroll, onScrollTo }}>
      <div
        style={style}
        className={cx(classes.scrollContainer, containerClassName, {
          [classes.isColumnLayout]: isColumnLayout,
        })}
      >
        <div className={cx(classes.navOptions, navClassName)}>
          {navOptions.map((option, idx) => {
            const key = option?.label + idx;
            if (React.isValidElement(option?.element)) return <div key={key}>{option.element}</div>;

            return (
              <div key={key} className={classes.label}>
                <NavOption
                  onClick={onScrollTo(option.id)}
                  active={activeScroll === option.id}
                  {...option}
                />
              </div>
            );
          })}
        </div>
        <div
          className={cx(classes.content, navContentClassName)}
          ref={refScroll}
          style={{
            height: height > 0 ? height : 'auto',
            position: 'relative',
          }}
          onScroll={onScroll}
        >
          <div ref={resizeRef} id="resizable-content">
            {children}
            <div id="negative-element" />
          </div>
        </div>
      </div>
    </NavScrollerContext.Provider>
  );
};
